import type { ComponentType } from 'react';
import { Link } from 'react-router';
import { ShieldCheck, ShieldAlert, Send, Scale, Activity, CheckCircle2 } from 'lucide-react';
import { useScan } from './ScanLayout';
import { PageHeader, Section } from '../components/Section';
import { ContactCta } from '../components/ContactCta';
import { RemediationPlan } from '../components/Remediation';
import type { PriorityFix } from '../types';

// Severity CSS var for a 0-10 module score. Kept in one place so the dial,
// the grade bars, and the Stat tones all read the same thresholds.
function scoreSevVar(n?: number): string {
  if (n == null) return '--t-ink-3';
  if (n >= 8) return '--t-sev-ok';
  if (n >= 6) return '--t-sev-low';
  if (n >= 4) return '--t-sev-med';
  return '--t-sev-crit';
}

// Circular 0-10 posture gauge. The arc fills proportionally and is colored by
// the overall posture token; the score reads in the middle.
function PostureDial({
  score, sevVar, label, Icon
}: {
  score: number;
  sevVar: string;
  label: string;
  Icon: ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean }>;
}) {
  const pct = Math.max(0, Math.min(1, score / 10));
  const R = 52;
  const CIRC = 2 * Math.PI * R;
  return (
    <div className="mx-auto flex flex-col items-center gap-3 sm:mx-0">
      <div className="relative h-36 w-36">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" aria-hidden="true">
          <circle cx="60" cy="60" r={R} fill="none" strokeWidth="10" stroke="var(--t-line-2)" />
          <circle
            cx="60" cy="60" r={R} fill="none" strokeWidth="10" strokeLinecap="round"
            stroke={`var(${sevVar})`}
            strokeDasharray={`${CIRC * pct} ${CIRC}`}
          />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="tnum font-display text-4xl font-bold leading-none text-ink">{score.toFixed(1)}</div>
            <div className="mt-1 text-[9px] font-bold tracking-[0.15em] text-ink-3">OUT OF 10</div>
          </div>
        </div>
      </div>
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold tracking-wider"
        style={{ color: `var(${sevVar})`, background: `color-mix(in oklab, var(${sevVar}) 13%, transparent)` }}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden={true} />
        {label}
      </span>
    </div>
  );
}

function GradeBar({ label, score }: { label: string; score?: number }) {
  const pct = score == null ? 0 : Math.max(0.02, Math.min(1, score / 10));
  const sevVar = scoreSevVar(score);
  return (
    <div className="flex items-center gap-3">
      <span className="w-14 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-ink-2">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-panel-2">
        <div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: `var(${sevVar})` }} />
      </div>
      <span className="tnum w-8 shrink-0 text-right text-sm font-bold text-ink">
        {score == null ? '—' : score.toFixed(1)}
      </span>
    </div>
  );
}

export function Executive() {
  const scan = useScan();
  const score = scan.overall_score;
  const exec = scan.executive;
  const tone = exec.posture;
  // "Good state" must not contradict active high/critical alerts shown in the
  // same view (e.g. SPF PermError or a takeover candidate that has no PriorityFix).
  const highAlerts = scan.alerts.filter(a => a.severity === 'high' || a.severity === 'critical');

  const palette = {
    excellent: { sevVar: '--t-sev-ok',   label: 'EXCELLENT SECURITY', Icon: ShieldCheck },
    good:      { sevVar: '--t-sev-low',  label: 'GOOD SECURITY',      Icon: ShieldCheck },
    fair:      { sevVar: '--t-sev-med',  label: 'FAIR SECURITY',      Icon: ShieldAlert },
    poor:      { sevVar: '--t-sev-crit', label: 'NEEDS ATTENTION',    Icon: ShieldAlert }
  }[tone];

  return (
    <>
      <PageHeader
        title="Executive Security Dashboard"
        subtitle={`Comprehensive security analysis for ${scan.domain}`}
      />

      {/* TL;DR — single sentence headline + 2-3 sentence narrative */}
      <section className="panel p-6">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Summary</p>
        <p className="mt-2 text-lg font-semibold leading-snug text-ink">
          {exec.headline}
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-ink-2">
          {exec.narrative}
        </p>
      </section>

      {/* Score band — posture dial + per-module grade bars. Sits directly under
          the summary so the headline number is the first thing after the TL;DR. */}
      <section className="panel p-6 sm:p-8">
        <div className="grid gap-8 sm:grid-cols-[auto_1fr] sm:items-center">
          <PostureDial score={score} sevVar={palette.sevVar} label={palette.label} Icon={palette.Icon} />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-3">
              Posture by domain
            </p>
            <div className="mt-3 space-y-2.5">
              <GradeBar label="DNS"   score={scan.modules.dns?.score} />
              <GradeBar label="Email" score={scan.modules.email?.score} />
              <GradeBar label="Web"   score={scan.modules.web?.score} />
              <GradeBar label="TLS"   score={scan.modules.ssl?.score} />
              <GradeBar label="WHOIS" score={scan.modules.whois?.score} />
            </div>
          </div>
        </div>
      </section>

      {/* Top 3 priority fixes */}
      {exec.top_fixes.length > 0 && (
        <Section title="Top Priority Fixes" accent="rose"
          status={{ label: `${exec.top_fixes.length} ITEMS`, tone: 'bad' }}>
          <ul className="space-y-3">
            {exec.top_fixes.map((f, i) => <FixRow key={f.id} f={f} index={i + 1} />)}
          </ul>
        </Section>
      )}

      {/* Copy-paste remediation plan, grouped by effort */}
      <RemediationPlan remediations={scan.remediations} />

      {/* Remediation CTA — turn findings into a PROTBYTE conversation */}
      {exec.top_fixes.length > 0 && (
        <Section accent="blue">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="max-w-xl text-sm leading-relaxed text-ink-2">
              Want these gaps closed by senior engineers? PROTBYTE remediates
              the findings and gets you assessment-ready, across CMMC, SOC 2, and more.
            </p>
            <ContactCta label="Fix this with PROTBYTE" />
          </div>
        </Section>
      )}

      {exec.top_fixes.length === 0 && highAlerts.length === 0 && (
        <Section accent="green">
          <p className="flex items-start gap-2 text-sm leading-relaxed text-ink-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sev-ok" strokeWidth={2.25} aria-hidden="true" />
            No high-priority remediations queued. Your domain is in a good state.
          </p>
        </Section>
      )}

      {exec.top_fixes.length === 0 && highAlerts.length > 0 && (
        <Section accent="amber">
          <p className="flex items-start gap-2 text-sm leading-relaxed text-ink-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-sev-med" strokeWidth={2.25} aria-hidden="true" />
            No ranked priority fixes were generated, but {highAlerts.length} high-severity
            alert{highAlerts.length === 1 ? '' : 's'} need review — see the Security Alerts panel.
          </p>
        </Section>
      )}

      <Recommendations />
    </>
  );
}

// Map a backend module name onto the corresponding results route path.
// Vulns/exposure share the /vuln route; techstack/vendors share /tech.
function moduleToPath(mod?: string): string | null {
  switch (mod) {
    case 'dns':        return '../dns';
    case 'email':      return '../email';
    case 'web':        return '../web';
    case 'ssl':        return '../ssl';
    case 'whois':      return '../whois';
    case 'subdomains': return '../subdomains';
    case 'techstack':
    case 'vendors':    return '../tech';
    case 'vulns':
    case 'exposure':   return '../vuln';
    default:           return null;
  }
}

function FixRow({ f, index }: { f: PriorityFix; index: number }) {
  const effortColor = { low: 'pill-ok', medium: 'pill-warn', high: 'pill-bad' }[f.effort];
  const impactLabel = {
    security: 'Security',
    deliverability: 'Email Deliverability',
    compliance: 'Compliance',
    brand: 'Brand Trust',
    availability: 'Availability'
  }[f.impact];
  // Lucide icon mapped to business impact dimension — communicates the
  // category at a glance without crowding the row.
  const ImpactIcon = {
    security:        ShieldAlert,
    deliverability:  Send,
    compliance:      Scale,
    brand:           ShieldCheck,
    availability:    Activity
  }[f.impact];
  return (
    <li className="rounded-lg border border-line bg-panel p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {(() => {
              const target = moduleToPath(f.module);
              return target ? (
                <Link
                  to={target}
                  title={`Open ${f.module} details`}
                  className="font-semibold text-ink transition hover:text-accent"
                >{f.title}</Link>
              ) : (
                <h3 className="font-semibold text-ink">{f.title}</h3>
              );
            })()}
            <span className={`pill ${effortColor}`}>EFFORT: {f.effort.toUpperCase()}</span>
            <span className="pill pill-info inline-flex items-center gap-1">
              <ImpactIcon className="h-3 w-3" strokeWidth={2.25} aria-hidden="true" />
              {impactLabel}
            </span>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{f.rationale}</p>
          {f.frameworks.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-ink-3">
                Maps to:
              </span>
              {f.frameworks.map(fr => (
                <Link
                  key={fr}
                  to="../compliance"
                  title="View this control on the Compliance page"
                  className="rounded-sm bg-panel-2 px-1.5 py-0.5 font-mono text-[10px] text-ink-2 hover:bg-accent/15 hover:text-accent"
                >
                  {fr}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function Recommendations() {
  const scan = useScan();
  if (scan.recommendations.length === 0) return null;

  const priColor = (p: string) =>
    p === 'high'      ? 'text-sev-crit' :
    p === 'medium'    ? 'text-sev-med'  :
    p === 'strategic' ? 'text-accent'   :
                        'text-ink-2';

  // Subtle vertical accent bar — colored by priority. Same visual cue as
  // the prefixed emoji had, but consistent across platforms and unobtrusive.
  const priBar = (p: string) =>
    p === 'high'      ? 'bg-sev-crit' :
    p === 'medium'    ? 'bg-sev-med'  :
    p === 'strategic' ? 'bg-accent'   :
                        'bg-line-2';

  return (
    <Section title="Strategic Recommendations" accent="blue">
      <p className="text-sm text-ink-2">
        Long-tail items that won't block today but compound over time.
      </p>
      <ul className="mt-4 space-y-2.5">
        {scan.recommendations.map((r, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span
              aria-hidden="true"
              className={`mt-1 h-3.5 w-0.5 shrink-0 rounded-full ${priBar(r.priority)}`}
            />
            <span>
              <span className={`font-semibold ${priColor(r.priority)}`}>
                {r.priority.toUpperCase()}:
              </span>{' '}
              <span className="text-ink-2">{r.text}</span>
              {moduleToPath(r.module) && (
                <>
                  {' '}
                  <Link to={moduleToPath(r.module)!} title={`Open ${r.module} details`}
                    className="whitespace-nowrap text-xs font-medium text-accent hover:text-accent-2">
                    view {r.module} →
                  </Link>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
